import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grid,
  Grow,
  makeStyles,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import clsx from 'clsx';
import React from 'react';
import ApplicationState from 'store/interfaces';
import { connect } from 'react-redux';
import { RolesEnum, CompanyData } from 'modules/authenticate/interfaces';
import { inAdminView, inOperatorView, inSuperAdminView } from 'utils/role';
import styles from './styles';

const useStyles = makeStyles(styles);

export interface ButtonGroupItem {
  disabled?: boolean;
  hidden?: boolean;
  icon?: React.ReactNode;
  id?: string;
  label: string;
  subOptions?: ButtonGroupItem[];
  styleSubOption?: any;
  dataCypress?: string;
  onClick?: () => void;
}

interface Props {
  companySelected: CompanyData | null;
  label: string;
  options: ButtonGroupItem[];
  role?: RolesEnum;
  style?: React.CSSProperties;
  defaultButtonStyle?: boolean;
  disabled?: boolean;
}

const CustomButton = ({
  companySelected,
  label,
  options,
  role,
  style,
  defaultButtonStyle,
  disabled,
}: Props) => {
  const classes = useStyles();

  const [open, setOpen] = React.useState(false);
  const [openSubOptions, setOpenSubOptions] = React.useState<string | null>(
    null
  );
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const anchorRef = React.useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    setOpen(prev => !prev);
    setOpenSubOptions(null);
  };

  const handleClose = (event: React.MouseEvent<Document, MouseEvent>) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
    setOpenSubOptions(null);
  };

  const handleCloseSubOptions = (
    event: React.MouseEvent<Document, MouseEvent>
  ) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }

    setOpenSubOptions(null);
  };

  const handleOpen = (
    key: string | undefined,
    event: React.MouseEvent<HTMLElement>,
    subOptions: boolean,
    onClick?: () => void
  ) => {
    if (onClick) {
      onClick();
    }

    if (!subOptions || !key) {
      setOpen(false);
      setOpenSubOptions(null);
    } else {
      if (!anchorRef.current) return;

      setAnchorEl(event.currentTarget);
      setOpenSubOptions(key);
    }
  };

  return (
    <Grid style={style}>
      <ButtonGroup
        disabled={disabled}
        variant="contained"
        className={clsx(classes.buttonGroupDefault, {
          [classes.buttonGroupAdmin]: inAdminView(role, companySelected),
          [classes.buttonGroupSuperAdmin]: inSuperAdminView(
            role,
            companySelected
          ),
          [classes.buttonGroupOperator]: inOperatorView(role, companySelected),
          [classes.buttonGroupCompanyContext]: companySelected,
          [classes.defaultButtonStyle]: defaultButtonStyle,
        })}
        ref={anchorRef}
      >
        <Button id="button-group-label" onClick={handleToggle}>
          {label}
        </Button>
        <Button id="button-group-icon" size="small" onClick={handleToggle}>
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        placement="bottom-start"
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        style={{ zIndex: 2 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList>
                  {options
                    .filter(item => !item.hidden)
                    .map(
                      ({
                        label,
                        onClick,
                        icon,
                        disabled,
                        subOptions,
                        id,
                        dataCypress,
                      }) => (
                        <>
                          <MenuItem
                            id={`btn-${label
                              .replace(/\s+/g, '-')
                              .toLowerCase()}`}
                            key={`${label}-${id}`}
                            onClick={event => {
                              handleOpen(label, event, !!subOptions, onClick);
                            }}
                            className={clsx(classes.menuItem, {
                              [classes.menuItemWithIcon]: !!icon,
                            })}
                            disabled={disabled}
                            data-cy={dataCypress}
                          >
                            {label}
                            {!!icon && icon}
                          </MenuItem>

                          <Popper
                            placement="right-start"
                            disablePortal
                            key={label}
                            open={openSubOptions === label}
                            anchorEl={anchorEl}
                            style={{ zIndex: 2 }}
                            modifiers={{
                              offset: { enabled: true, offset: '0, 4' },
                            }}
                          >
                            {!!subOptions && (
                              <Paper key={label}>
                                <ClickAwayListener
                                  onClickAway={handleCloseSubOptions}
                                >
                                  <MenuList>
                                    {subOptions
                                      .filter(subOption => !subOption.hidden)
                                      .map(subOption => (
                                        <MenuItem
                                          id={
                                            subOption.id ||
                                            `option-${subOption.label
                                              .replace(/\s+/g, '-')
                                              .toLowerCase()}`
                                          }
                                          onClick={event => {
                                            handleOpen(
                                              subOption.label,
                                              event,
                                              !!subOption.subOptions,
                                              subOption.onClick
                                            );
                                          }}
                                          key={subOption.label}
                                          className={classes.menuItem}
                                          style={subOption.styleSubOption}
                                        >
                                          {subOption.label}
                                        </MenuItem>
                                      ))}
                                  </MenuList>
                                </ClickAwayListener>
                              </Paper>
                            )}
                          </Popper>
                        </>
                      )
                    )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data, companySelected },
}: ApplicationState) => ({
  role: data?.role,
  companySelected,
});

export default connect(mapStateToProps, {})(CustomButton);
